import React, { useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert } from '@mui/material';
import classes from '../../styling/components/PhishingMailPreview.module.css';
import { useTranslation } from 'react-i18next';
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView';
import 'froala-editor/css/froala_style.min.css';
import 'froala-editor/css/froala_editor.pkgd.min.css';
import 'froala-editor/js/plugins.pkgd.min.js';
import '../../styling/preview/css/froala_styles.css';
import { sendPhishingMail } from '../../api/phishingDemoClient';

interface PhishingMailPreviewProps {
    subject: string;
    body: string;
    sender: string;
}

const getInitials = (sender: string): string => {
    // Remove email address if present (anything between < and >)
    const nameOnly = sender.replace(/<[^>]*>/g, '').trim();
    
    // Split by spaces and filter out empty strings
    const words = nameOnly.split(/\s+/).filter(word => word.length > 0);
    
    if (words.length >= 2) {
        // If there are 2 or more words, take first letter of first two words
        return (words[0][0] + words[1][0]).toUpperCase();
    } else if (words.length === 1) {
        // If there's only one word, take its first letter
        return words[0][0].toUpperCase();
    }
    
    // Fallback: take first character of the entire string
    return sender.charAt(0).toUpperCase();
};

export const PhishingMailPreview: React.FC<PhishingMailPreviewProps> = ({ subject, body, sender }) => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [sendSuccess, setSendSuccess] = useState(false);

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setEmail(value);
        setIsEmailValid(value === '' || validateEmail(value));
        setSendError(null);
        setSendSuccess(false);
    };

    const handleSendMail = async () => {
        if (!validateEmail(email)) {
            setIsEmailValid(false);
            return;
        }

        setIsSending(true);
        setSendError(null);
        setSendSuccess(false);

        try {
            const response = await sendPhishingMail(subject, body, email);
            if (!response.ok) {
                throw new Error(t('phishingDemo.mailError', 'Error sending mail'));
            }
            setSendSuccess(true);
            setEmail('');
        } catch (error) {
            setSendError(t('phishingDemo.mailError', 'Error sending mail'));
        } finally {
            setIsSending(false);
        }
    };

    return (
        <Box className={classes.previewContainer}>
            <Paper className={classes.emailContainer}>
                <div className={classes.mailHeaderBar}>
                    <div className={classes.mailHeaderBarIcon}>
                        <img src="/assets/img/minimize-window.svg" width="16" height="16" alt="minimize" />
                    </div>
                    <div className={classes.mailHeaderBarIcon}>
                        <img src="/assets/img/maximize-window.svg" width="16" height="16" alt="maximize" />
                    </div>
                    <div className={classes.mailHeaderBarIcon}>
                        <img src="/assets/img/close-window.svg" width="16" height="16" alt="close" />
                    </div>
                </div>
                <div className={classes.mailHeaderInfo}>
                    <div className={classes.mailHeaderSubject}>
                        <strong>{subject}</strong>
                    </div>
                    <div className={classes.senderReceiverContainer}>
                        <div className={classes.senderIcon}>{getInitials(sender)}</div>
                        <div>
                            <div className={classes.mailHeaderSender}>
                                <strong>{sender}</strong>
                            </div>
                            <div className={classes.mailHeaderReceiver}>
                                <strong>{t('phishingDemo.to', 'To')}:</strong> [{t('phishingDemo.yourEmailAddress', 'Your email address')}]
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.mailBody}>
                    <FroalaEditorView
                        model={body}
                    />
                </div>
            </Paper>
            
            <Box className={classes.sendMailContainer}>
                <Typography variant="h6" className={classes.sendMailTitle}>
                    {t('phishingDemo.sendMailTitle', 'Phish me')}
                </Typography>
                <Typography variant="body2" className={classes.sendMailDescription}>
                    {t('phishingDemo.sendMailDescription', 'Enter your email address to receive your mail directly in your inbox.')}
                </Typography>
                
                {sendError && (
                    <Alert severity="error" onClose={() => setSendError(null)} sx={{ mb: 2 }}>
                        {sendError}
                    </Alert>
                )}
                
                {sendSuccess && (
                    <Alert severity="success" onClose={() => setSendSuccess(false)} sx={{ mb: 2 }}>
                        {t('phishingDemo.mailSent', 'Mail sent successfully')}
                    </Alert>
                )}
                
                <Box className={classes.sendMailForm}>
                    <TextField
                        fullWidth
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        placeholder={t('phishingDemo.emailPlaceholder', 'your.email@example.com')}
                        error={!isEmailValid}
                        helperText={!isEmailValid ? t('phishingDemo.invalidEmail', 'Please enter a valid email address') : ''}
                        className={classes.emailInput}
                        disabled={isSending}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                '& fieldset': {
                                    borderColor: 'var(--look2x-lilac)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'var(--look2x-lilac)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'var(--look2x-lilac)',
                                },
                            },
                            '& .MuiInputBase-input': {
                                color: '#ffffff',
                            },
                            '& .MuiInputLabel-root': {
                                color: 'var(--look2x-lilac)',
                            },
                            '& .MuiFormHelperText-root': {
                                color: '#ff6b6b',
                            },
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleSendMail}
                        disabled={!email || !isEmailValid || isSending}
                        className={classes.sendButton}
                        sx={{
                            backgroundColor: 'var(--look2x-lilac)',
                            color: 'var(--look2x-blue)',
                            '&:hover': {
                                backgroundColor: '#8a97c4',
                            },
                            '&:disabled': {
                                backgroundColor: 'rgba(160, 174, 217, 0.5)',
                                color: 'rgba(14, 36, 69, 0.5)',
                            },
                        }}
                    >
                        {isSending 
                            ? t('phishingDemo.sendingMail', 'Sending...') 
                            : t('phishingDemo.sendMail', 'Send Mail')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}; 